

































































































import {
  computed,
  defineComponent,
  toRefs,
  watch,
  ref,
  Ref,
  reactive,
  WritableComputedRef,
} from '@vue/composition-api';
import { ErrorCircleIcon } from 'tdesign-icons-vue';
import ConstantConfig from '@/renderer/scripts/utils/ConstantConfig';
import { formatSize, formatSize2Byte } from '@/renderer/scripts/utils/tools/index';
export default defineComponent({
  name: 'spaceConfig',
  components: {
    ErrorCircleIcon,
  },
  props: {
    spaceSizeInfo: Object,
    showUserRole: {
      type: Boolean,
      default: false,
    },
    operType: {
      type: String,
      default: '',
    },
    role: {
      type: String,
      required: false,
    },
    userInfo: {
      type: Object,
      required: false,
    },
  },
  setup(props, context) {
    const store = context.root.$store;
    const { sizeMap } = ConstantConfig;
    const sizeOptions = Object.keys(sizeMap).map(key => ({
      label: key,
      value: key,
    }));
    const currentUser = computed(() => store.getters.userBaseInfo);
    const currentIsSuperAdmin = computed(() => currentUser.value.role === 'superAdmin');
    const currentIsAdmin = computed(() => currentUser.value.role === 'admin');
    const editingSelf = computed(() => props.userInfo && props.userInfo.id === currentUser.value.userId);

    // 空间设置
    const { spaceSizeInfo }: any = toRefs(props);

    // 自己不能修改自己的权限；系管不能修改用户的权限
    const roleDisabled = computed(() => {
      if (editingSelf.value) return true;
      if (currentIsAdmin.value) return true;
      return false;
    });
    const sizeInfo = computed(() => formatSize(spaceSizeInfo.value.personalSpaceQuotaSize, true));
    const size = ref(`${sizeInfo.value.size}`);
    const unit = ref(sizeInfo.value.unit);
    const sizeInput: WritableComputedRef<string> = computed({
      get() {
        return size.value;
      },
      set(val: string) {
        const newVal = val.replace(/\D/g, '');
        size.value = newVal as string;
      },
    });
    const allowPersonalSpace = ref(spaceSizeInfo.value.allowPersonalSpace); // 是否开启个人空间
    const specifySpace = ref(Boolean(spaceSizeInfo.value.personalSpaceQuotaSize)); // 是否指定额度
    const { orgAvailableSpace, orgAvailableSpaceInfo } = spaceSizeInfo.value;
    const defaultSize = ConstantConfig.minPersonalSpaceSize; // 个人空间最小限制
    const minSizeStr = `${formatSize(defaultSize).size}${formatSize(defaultSize).unit}`;
    const userRole: Ref<string> = ref(props.role || 'user');
    const sizeInvalid = computed(() => {
      const sizeStr = formatSize2Byte(size.value, unit.value);
      // 不分配个人空间或设置共享剩余额度时 不需要校验
      if (!allowPersonalSpace.value || !specifySpace.value) return false;
      return sizeStr > orgAvailableSpace || sizeStr < defaultSize;
    });

    // 配置项未改动(对比初始值)
    const configNotChanged = computed(() => {
      const sizeStr = specifySpace.value ? `${formatSize2Byte(size.value, unit.value)}` : '';
      const defaultSpecifySpace = Boolean(spaceSizeInfo.value.personalSpaceQuotaSize);
      return (
        allowPersonalSpace.value === spaceSizeInfo.value.allowPersonalSpace &&
        sizeStr === spaceSizeInfo.value.personalSpaceQuotaSize &&
        specifySpace.value === defaultSpecifySpace
      );
    });

    // 保存过的值暂存
    const tmpConfig = reactive({
      allowPersonalSpace: spaceSizeInfo.value.allowPersonalSpace,
      personalSpaceQuotaSize: spaceSizeInfo.value.personalSpaceQuotaSize,
      specifySpace: Boolean(spaceSizeInfo.value.personalSpaceQuotaSize),
    });

    // 配置项保存过
    const configSaved = computed(() => {
      const sizeStr = specifySpace.value ? `${formatSize2Byte(size.value, unit.value)}` : '';
      return (
        allowPersonalSpace.value === tmpConfig.allowPersonalSpace &&
        sizeStr === tmpConfig.personalSpaceQuotaSize &&
        specifySpace.value === tmpConfig.specifySpace
      );
    });

    const showPopconfirm = ref(false);

    const methods = {
      onRoleChange(val: string) {
        context.emit('role-changed', val);
      },
      confirmClear(confirm: boolean) {
        if (confirm) {
          methods.saveSize();
        } else {
          showPopconfirm.value = false;
          allowPersonalSpace.value = true;
        }
      },
      saveSize() {
        // 原先有个人空间的用户删除个人空间，提示管理员。
        if (props.operType === 'update' && props.userInfo?.allowPersonalSpace === true) {
          if (allowPersonalSpace.value === false && showPopconfirm.value === false) {
            showPopconfirm.value = true;
            return;
          }
          if (showPopconfirm.value) {
            showPopconfirm.value = false;
          }
        }
        const sizeStr = `${formatSize2Byte(size.value, unit.value)}`;
        Object.assign(tmpConfig, {
          allowPersonalSpace: allowPersonalSpace.value,
          personalSpaceQuotaSize: specifySpace.value ? sizeStr : '',
          specifySpace: specifySpace.value,
        });
        context.emit('size-saved', tmpConfig);
      },
    };
    watch(
      () => specifySpace.value,
      val => {
        if (val) {
          // 设置指定额度时，默认值取系统初始化值
          if (!spaceSizeInfo.value.personalSpaceQuotaSize) {
            const defaultSize = store.getters.defaultUserOptions.personalSpaceQuotaSize || `${100 * 1024 * 1024}`;
            const defaultSizeInfo = formatSize(defaultSize, true);
            size.value = `${defaultSizeInfo.size}`;
            unit.value = defaultSizeInfo.unit;
          }
        } else {
          const defaultSizeInfo = formatSize(spaceSizeInfo.value.personalSpaceQuotaSize, true);
          size.value = `${defaultSizeInfo.size}`;
          unit.value = defaultSizeInfo.unit;
        }
      },
    );

    return {
      sizeOptions,
      size,
      showPopconfirm,
      unit,
      sizeInput,
      allowPersonalSpace,
      specifySpace,
      orgAvailableSpace,
      orgAvailableSpaceInfo,
      minSizeStr,
      userRole,
      currentIsSuperAdmin,
      editingSelf,
      roleDisabled,
      sizeInvalid,
      configNotChanged,
      configSaved,
      ...methods,
    };
  },
});
